// OTEL tracing loader. Lazily imports @opentelemetry/api and grabs a
// tracer handle from the global provider. We deliberately do NOT spin
// up a NodeSDK from here — that drags in @opentelemetry/sdk-node (heavy
// and a deployment choice). Callers who want tracing install the SDK
// themselves and configure the global provider; we just consume it.
//
// Scope is intentionally minimal: a single tracer per service, manual
// spans via withSpan(). Auto-instrumentation (HTTP/Express/gRPC) is
// available via @opentelemetry/auto-instrumentations but is a separate
// decision per app.

import { readEnv } from './env.js';
import type { ObservabilityOptions, OtelSpan, OtelTracer } from './types.js';

// Minimal shape of @opentelemetry/api we depend on. Defined locally so
// the package's type-check passes without installing OTEL; the runtime
// import resolves the real package or fails gracefully (caught below).
interface OtelApiSpan {
  setAttribute(key: string, value: string | number | boolean): void;
  recordException(err: unknown): void;
  end(): void;
}
interface OtelApi {
  trace: {
    getTracer(
      name: string,
      version?: string,
    ): {
      startActiveSpan<T>(
        name: string,
        opts: Record<string, unknown>,
        fn: (span: OtelApiSpan) => T,
      ): T;
    };
  };
}

let tracer: OtelTracer | null = null;
let initAttempted = false;

export async function initTracing(opts: ObservabilityOptions): Promise<OtelTracer | null> {
  if (tracer || initAttempted) return tracer;
  initAttempted = true;

  const endpoint = opts.otelEndpoint ?? readEnv('OTEL_EXPORTER_OTLP_ENDPOINT');
  if (!endpoint) return null;

  const api = await tryImportOtelApi();
  if (!api) {
    console.warn(
      '[observability] OTEL_EXPORTER_OTLP_ENDPOINT set but @opentelemetry/api not installed; ' +
        'install it (and a tracer provider) to enable tracing',
    );
    return null;
  }

  const otelTracer = api.trace.getTracer(opts.appName, opts.appVersion);

  // Bridge OTEL's tracer API into our minimal OtelTracer shape.
  tracer = {
    startActiveSpan<T>(
      name: string,
      fn: (span: OtelSpan) => T | Promise<T>,
      opts?: Record<string, unknown>,
    ): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        otelTracer.startActiveSpan(name, opts ?? {}, (span) => {
          const wrapped: OtelSpan = {
            setAttribute(k, v) {
              span.setAttribute(k, v as unknown as string | number | boolean);
            },
            recordException(err) {
              span.recordException(err);
            },
            end() {
              span.end();
            },
          };
          try {
            const result = fn(wrapped);
            if (result instanceof Promise) {
              result.then(resolve, (err: unknown) => {
                span.recordException(err);
                span.end();
                reject(err);
              });
            } else {
              span.end();
              resolve(result);
            }
          } catch (err) {
            span.recordException(err);
            span.end();
            reject(err);
          }
        });
      });
    },
  };
  return tracer;
}

async function tryImportOtelApi(): Promise<OtelApi | null> {
  try {
    // Variable spec so TS doesn't statically resolve the module — the
    // package is an optional peer dep apps install themselves.
    const spec = '@opentelemetry/api';
    const mod = (await import(/* @vite-ignore */ spec)) as unknown;
    return mod as OtelApi;
  } catch {
    return null;
  }
}

/** Run fn inside a span. When tracing is off, fn is still called — just unwrapped. */
export async function withOtelSpan<T>(
  name: string,
  fn: (span: OtelSpan | null) => T | Promise<T>,
  attrs?: Record<string, unknown>,
): Promise<T> {
  if (!tracer) {
    return (await fn(null)) as T;
  }
  return tracer.startActiveSpan(
    name,
    async (span) => {
      if (attrs) {
        for (const [k, v] of Object.entries(attrs)) span.setAttribute(k, v);
      }
      return fn(span);
    },
    {},
  );
}

export async function shutdownTracing(): Promise<void> {
  tracer = null;
  initAttempted = false;
}
